import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,  } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import AttackTypesModal from './modals/attackTypes/attackModal';
import DeleteAttackTypeModal from './modals/attackTypes/deleteAttackTypeModal';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';
import { sortAdminItems } from '../common/utils/tableUtil';
import SearchBar from '../common/search/SearchBar';

const AttacksTable = ({ items, onToggle, sortConfig, onSortChange, fetchAttacks, setAttacks }) => {

  const handleUpdate = (attack) =>  {    
    toast.success(`${attack.label} Updated`, { autoClose: 3000 });
    fetchAttacks();
  };

  const handleDelete = (attack) =>  {
    setAttacks(items.filter(items => items.id !== attack.id));
    toast.success(`${attack.label} Deleted`, { autoClose: 3000 });
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['Value', 'Label', 'Active'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', attackSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}
              <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.label}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.value}</TableCell>
                <TableCell> <Switch checked={!!item.active} onChange={(e) => onToggle(item, 'active', e.target.checked)} /> </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1}>
                        <AttackTypesModal attack={ item } onTriggerUpdate={handleUpdate} />
                        <DeleteAttackTypeModal attack={ item } onTriggerDelete={handleDelete} />
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function AttackTypes() {
    const [attacks, setAttacks] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchAttacks();
    }, []);

    const fetchAttacks = async () => {
        try {
                
            const response = await fetch(`${process.env.REACT_APP_API}/attackTypes/`);            
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                setAttacks(json.data);
            }
        } catch (error) {
            toast.error(error);
        } 
    };

    const handleCreate = (attack) =>  {
      toast.success(`${attack.label} Attack Type Created`, { autoClose: 3000 });
      fetchAttacks();
    };


    const handleUpdate = async (attack) => {
         const res = await fetch(`${process.env.REACT_APP_API}/attackTypes/update`, {
             method: 'PUT',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify(attack)
         });
         if (!res.ok) {
            toast.error(`Failed to update attack type : ${res.status}`);
          } else {
            setAttacks((prev) => prev.map((p) => (p.id === attack.id ? attack : p)));
         
            if ( attack.active ) {
              toast.success(`${attack.label} Attack Type Has Been Activated`, { autoClose: 3000 });
            } else {
              toast.success(`${attack.label} Attack Type Has Been De-Activated`, { autoClose: 3000 });
            }
            
          }
     };

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    };

    const filtered = attacks?.filter((item) => {
        const s = searchQuery.toLowerCase();
        return ['label', 'value'].some((field) => item[field]?.toLowerCase().includes(s));
    });

  const sorted = sortAdminItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader />
            <PageHeader title="Attack Types" />
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Attack Types..."
                      label="Search Attack Types..."
                      width={200}
                  />                
            </Stack>
          
            <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                <AttacksTable
                    items={paginated}
                    onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    fetchAttacks={fetchAttacks}
                    setAttacks={setAttacks}
                />
                <TablePagination
                    component="div"
                    count={sorted.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                />
            </TableContainer>
            <AttackTypesModal attack='' onTriggerCreate={handleCreate} />
        </Box>
    </Container>
  )
}
